<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <a href="/health" class="breadcrumb-link">Health Services</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Print Food Hygiene Licence</span>
            </nav>
            <h1 class="page-title">Print Food Hygiene Licence</h1>
            <p class="page-subtitle">Enter your Business ID to view and print your Food Hygiene Licence</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Card -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <h3>Food Hygiene Licence</h3>
                </div>
                <p class="info-description">
                  Your Food Hygiene Licence is an official document certifying that your business premises meet the required public health and food safety standards for operation within the county.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Health Compliance</strong>
                      <p>Certified food safety & hygiene standards</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Annual Validity</strong>
                      <p>Valid for one calendar year</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Instant Download</strong>
                      <p>Print or save as PDF</p>
                    </div>
                  </div>
                </div>

                <div class="info-help">
                  <h4>Need Help?</h4>
                  <p>If you don't remember your Business ID, you can find it on:</p>
                  <ul>
                    <li>Previous licence documents</li>
                    <li>Payment receipts</li>
                    <li>SMS notifications</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Right Side - Form/Details -->
            <div class="form-panel">
              <!-- Search Form -->
              <div v-if="searchLicence" class="form-card">
                <div class="form-card-header">
                  <div class="form-step-indicator">
                    <span class="step-number">1</span>
                    <span class="step-text">Enter Business ID</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p class="form-intro">
                    Enter your Business ID to retrieve your Food Hygiene Licence details. The Business ID was provided when you registered your business.
                  </p>

                  <div class="search-form">
                    <div class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Business ID
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="businessID"
                            type="text"
                            class="form-input"
                            placeholder="e.g., 4452"
                            @keyup.enter="fetchLicence"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">Enter the Business ID exactly as it appears on your documents</span>
                    </div>

                    <!-- Error Message -->
                    <div v-if="message" class="alert alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{{ message }}</span>
                      <button class="alert-close" @click="message = null">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>

                    <button
                        @click="fetchLicence"
                        class="btn btn-primary btn-block"
                        :disabled="loading"
                    >
                      <div v-if="loading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>{{ loading ? 'Searching...' : 'Find Licence' }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Licence Details -->
              <div v-if="licenceDetails" class="form-card permit-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <div class="permit-status" :class="getStatusClass(permit.stageStatus)">
                    <span class="status-dot"></span>
                    {{ permit.stageStatus || 'Active' }}
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Business Header -->
                  <div class="business-header">
                    <div class="business-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div class="business-info">
                      <h2 class="business-name">{{ permit.businessName }}</h2>
                      <span class="business-id">ID: {{ permit.businessID }}</span>
                    </div>
                  </div>

                  <!-- Health Licence Badge -->
                  <div class="licence-badge">
                    <div class="licence-badge-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                    </div>
                    <div class="licence-badge-content">
                      <strong>{{ county.healthPermitTitle || 'Food Hygiene Licence' }}</strong>
                      <p>{{ permit.feeDescription || 'Food general Shop retailing per year' }}</p>
                    </div>
                  </div>

                  <!-- Permit Details Grid -->
                  <div class="details-grid">
                    <div class="detail-item">
                      <span class="detail-label">Business Description</span>
                      <span class="detail-value">{{ permit.businessDes || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Bill Number</span>
                      <span class="detail-value">{{ permit.billNo || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Location</span>
                      <span class="detail-value">{{ permit.subCountyName }}, {{ permit.wardName }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Physical Address</span>
                      <span class="detail-value">{{ permit.physicalAddress || permit.buildingName || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Owner</span>
                      <span class="detail-value">{{ permit.fullNames || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Phone</span>
                      <span class="detail-value">{{ permit.ownerPhone || 'N/A' }}</span>
                    </div>
                  </div>

                  <!-- Fee Details -->
                  <div class="fee-card">
                    <div class="fee-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                      <span>Payment Details</span>
                    </div>
                    <div class="fee-grid">
                      <div class="fee-item">
                        <span class="fee-label">Bill Total</span>
                        <span class="fee-value">KES {{ formatAmount(permit.billTotal) }}</span>
                      </div>
                      <div class="fee-item">
                        <span class="fee-label">Amount Paid</span>
                        <span class="fee-value paid">KES {{ formatAmount(permit.receiptAmount) }}</span>
                      </div>
                      <div class="fee-item">
                        <span class="fee-label">Balance</span>
                        <span class="fee-value" :class="{ 'balance-due': Number(permit.billBalance) > 0 }">KES {{ formatAmount(permit.billBalance) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Validity Card -->
                  <div class="validity-card">
                    <div class="validity-header">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>Licence Validity</span>
                    </div>
                    <div class="validity-dates">
                      <div class="validity-date">
                        <span class="date-label">Issue Date</span>
                        <span class="date-value">{{ formatDate(permit.startDate) }}</span>
                      </div>
                      <div class="validity-arrow">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </div>
                      <div class="validity-date">
                        <span class="date-label">Expiry Date</span>
                        <span class="date-value" :class="{ 'expired': isExpired }">{{ formatDate(permit.endDate) }}</span>
                      </div>
                    </div>
                    <div v-if="isExpired" class="expiry-warning">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                      <span>This licence has expired. Please renew to continue operations.</span>
                    </div>
                  </div>

                  <!-- Health Inspection Note -->
                  <div v-if="permit.comments" class="inspection-note">
                    <div class="inspection-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M9 11l3 3L22 4"></path>
                        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                      </svg>
                      <span>Health Inspection Remarks</span>
                    </div>
                    <p>{{ permit.comments }}</p>
                  </div>

                  <!-- Print Button -->
                  <div class="action-buttons">
                    <button @click="printLicence" class="btn btn-primary btn-lg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      <span>Print Licence</span>
                    </button>

                    <button v-if="isExpired" @click="renewLicence" class="btn btn-success btn-lg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                      </svg>
                      <span>Renew Licence</span>
                    </button>
                  </div>

                  <!-- Additional Info -->
                  <div class="additional-info">
                    <div class="info-row">
                      <span class="info-label">Issuing Officer</span>
                      <span class="info-value">{{ permit.issuingOfficer || 'County Health Office' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Date of Issue</span>
                      <span class="info-value">{{ formatDate(permit.dateOfIssue) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Duration</span>
                      <span class="info-value">{{ permit.duration || 'N/A' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Conservancy</span>
                      <span class="info-value">{{ permit.conservancy || 'N/A' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Fire Licence</span>
                      <span class="info-value">{{ permit.fireLicence || 'N/A' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { execute, health } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PrintFoodHygieneLicence",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      businessID: '',
      message: null,
      loading: false,
      searchLicence: true,
      licenceDetails: false,
      permit: {
        id: null,
        billNo: null,
        businessID: null,
        businessName: null,
        duration: null,
        billTotal: null,
        receiptAmount: null,
        billBalance: null,
        startDate: null,
        endDate: null,
        statusID: null,
        status: null,
        printable: null,
        issuingOfficer: null,
        designation: null,
        dateOfIssue: null,
        stageStatus: null,
        comments: null,
        incomeTypeId: null,
        feeID: null,
        subCountyName: null,
        wardName: null,
        dateCreated: null,
        subCountyID: null,
        wardID: null,
        plotNumber: null,
        physicalAddress: null,
        buildingName: null,
        buildingOccupancy: null,
        floorNo: null,
        roomNo: null,
        premiseSize: null,
        numberOfEmployees: null,
        tonnage: null,
        businessDes: null,
        businessCategory: null,
        businessSubCategory: null,
        incomeTypeID: null,
        businessEmail: null,
        postalAddress: null,
        postalCode: null,
        businessPhone: null,
        contactPersonNames: null,
        contactPersonIDNo: null,
        businessRole: null,
        contactPersonPhone: null,
        contactPersonEmail: null,
        fullNames: null,
        ownerID: null,
        ownerPhone: null,
        ownerEmail: null,
        kraPin: null,
        createdBy: null,
        createdByIDNo: null,
        lat: null,
        lng: null,
        lastRenewed: null,
        permitsCount: null,
        conservancy: null,
        fireLicence: null,
        liquorLicence: null,
        businessType: null,
        feeDescription: null,
        unitFeeAmount: null,
        accountDesc: null
      },
      county: {
        healthPermitTitle: null
      }
    };
  },

  computed: {
    isExpired() {
      if (!this.permit.endDate) return false;
      return new Date(this.permit.endDate) < new Date();
    }
  },


  mounted() {
    const businessID = this.$route.params.businessID || this.$route.query.businessID;
    if (businessID) {
      this.businessID = businessID;
      this.fetchLicence();
    }
  },



  methods: {
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      } catch {
        return dateStr;
      }
    },

    formatAmount(amount) {
      if (!amount) return '0.00';
      return Number(amount).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getStatusClass(status) {
      if (!status) return 'active';
      const s = status.toLowerCase();
      if (s.includes('active') || s.includes('approved')) return 'active';
      if (s.includes('expired')) return 'expired';
      if (s.includes('pending')) return 'pending';
      return 'active';
    },

    goBack() {
      this.searchLicence = true;
      this.licenceDetails = false;
      this.message = null;
    },

    printLicence() {
      localStorage['params'] = JSON.stringify({
        businessID: this.permit.businessID
      });
      const routeData = this.$router.resolve({ name: 'health-permit' });
      window.open(routeData.href, '_blank');
    },

    renewLicence() {
      this.$router.push({ name: 'renew-health-permit', params: { businessID: this.permit.businessID } });
    },

    fetchLicence() {
      if (this.businessID === '') {
        this.message = "Please enter a Business ID";
        return;
      }

      this.loading = true;
      this.message = null;

      const data = new FormData();
      data.append("function", "printPermit");
      data.append("businessID", this.businessID);

      execute(data, health)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.searchLicence = false;
              this.licenceDetails = true;
              this.permit = res.data.data.permit;
              if (res.data.data.county) {
                this.county = res.data.data.county;
              }
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message;
          });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  background: #d1fae5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  flex-shrink: 0;
}

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.info-help {
  padding: 20px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
}

.info-help h4 {
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 8px;
}

.info-help p {
  font-size: 13px;
  color: #a16207;
  margin-bottom: 12px;
}

.info-help ul {
  margin: 0;
  padding-left: 20px;
}

.info-help li {
  font-size: 13px;
  color: #a16207;
  margin-bottom: 4px;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-step-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 32px;
  height: 32px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.permit-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.permit-status.active {
  background: #dbeafe;
  color: #2563eb;
}

.permit-status.expired {
  background: #fee2e2;
  color: #dc2626;
}

.permit-status.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.input-with-icon {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.form-input::placeholder {
  color: #94a3b8;
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
  opacity: 0.7;
}

.alert-close:hover { opacity: 1; }

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block {
  width: 100%;
}

.btn-lg {
  padding: 16px 32px;
  font-size: 16px;
}

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
  transform: translateY(-1px);
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Business Header */
.business-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.business-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.business-name {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.business-id {
  font-size: 14px;
  color: #2563eb;
  font-weight: 600;
  font-family: monospace;
  background: rgba(37, 99, 235, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
}

/* Licence Badge */
.licence-badge {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 12px;
  margin-bottom: 24px;
}

.licence-badge-icon {
  width: 44px;
  height: 44px;
  background: #2563eb;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.licence-badge-content strong {
  display: block;
  font-size: 15px;
  color: #1e40af;
  margin-bottom: 2px;
}

.licence-badge-content p {
  font-size: 13px;
  color: #3b82f6;
  margin: 0;
}

/* Details Grid */
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.detail-item {
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

/* Fee Card */
.fee-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
}

.fee-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 16px;
}

.fee-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.fee-item {
  text-align: center;
  padding: 12px;
  background: white;
  border-radius: 10px;
}

.fee-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.fee-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.fee-value.paid {
  color: #059669;
}

.fee-value.balance-due {
  color: #dc2626;
}

/* Validity Card */
.validity-card {
  background: #eff6ff;
  border: 2px solid #93c5fd;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
}

.validity-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2563eb;
  font-weight: 600;
  margin-bottom: 16px;
}

.validity-dates {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 16px;
  border-radius: 10px;
}

.validity-date {
  text-align: center;
}

.date-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.date-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.date-value.expired {
  color: #dc2626;
}

.validity-arrow {
  color: #2563eb;
}

.expiry-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
}

/* Inspection Note */
.inspection-note {
  padding: 16px 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  margin-bottom: 24px;
}

.inspection-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2563eb;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
}

.inspection-note p {
  font-size: 14px;
  color: #1e40af;
  margin: 0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.action-buttons .btn {
  flex: 1;
}

/* Additional Info */
.additional-info {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  color: #64748b;
}

.info-value {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  .form-card-body {
    padding: 24px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .fee-grid {
    grid-template-columns: 1fr;
  }

  .validity-dates {
    flex-direction: column;
    gap: 16px;
  }

  .validity-arrow {
    transform: rotate(90deg);
  }

  .action-buttons {
    flex-direction: column;
  }

  .business-header {
    flex-direction: column;
    text-align: center;
  }

  .page-title {
    font-size: 26px;
  }
}
</style>